import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { MonitorSmartphone, MoreVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from '@/components/ui/sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ME_QUERY_KEY } from '@/features/auth/api';
import { SessionList } from '@/features/sessions/SessionList';
import { useCloseSessions, useSessions, type SessionScope } from '@/features/sessions/api';
import { SettingsCard } from '@/features/settings/SettingsCard';

export function SessionsSection({ className }: { className?: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const sessions = useSessions('me');

  // Mirror useLogout's cleanup: dropping the `me` query sends App.tsx back to the login screen.
  const onAllClosed = () => {
    qc.setQueryData(ME_QUERY_KEY, null);
    qc.removeQueries({
      predicate: (query) => query.queryKey[0] !== ME_QUERY_KEY[0],
    });
  };

  const list = sessions.data;

  return (
    <SettingsCard
      icon={MonitorSmartphone}
      title={t('sessions.title')}
      description={t('sessions.description')}
      className={className}
      action={
        list && list.length > 0 ? (
          <BulkActions scope="me" count={list.length} onAllClosed={onAllClosed} />
        ) : undefined
      }
    >
      <SessionList scope="me" onAllClosed={onAllClosed} />
    </SettingsCard>
  );
}

/**
 * Header overflow menu for the caller's own sessions: close the others (keeping this device) or
 * close everything (which signs this device out too). Each item routes through a confirm dialog.
 * "Close others" only appears when there's more than one session to close.
 */
function BulkActions({
  scope,
  count,
  onAllClosed,
}: {
  scope: SessionScope;
  count: number;
  onAllClosed?: () => void;
}) {
  const { t } = useTranslation();
  const [dialog, setDialog] = useState<'others' | 'all' | null>(null);
  const closeOthers = useCloseSessions(scope, { keepCurrent: true });
  const closeAll = useCloseSessions(scope, { keepCurrent: false });
  const busy = closeOthers.isPending || closeAll.isPending;

  const onCloseOthers = () =>
    closeOthers.mutate(undefined, {
      onSuccess: () => toast.success(t('sessions.toasts.others_closed')),
      onError: () => toast.error(t('sessions.toasts.close_failed')),
    });

  const onCloseAll = () =>
    closeAll.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('sessions.toasts.all_closed'));
        onAllClosed?.();
      },
      onError: () => toast.error(t('sessions.toasts.close_failed')),
    });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon-sm"
            aria-label={t('sessions.manage')}
            disabled={busy}
          >
            <MoreVertical />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {count > 1 ? (
            <DropdownMenuItem onSelect={() => setDialog('others')}>
              {t('sessions.close_others')}
            </DropdownMenuItem>
          ) : null}
          <DropdownMenuItem variant="destructive" onSelect={() => setDialog('all')}>
            {t('sessions.close_all')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ConfirmDialog
        open={dialog === 'others'}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
        title={t('sessions.confirm.close_others_title')}
        description={t('sessions.confirm.close_others_description')}
        confirmLabel={t('sessions.close_others')}
        onConfirm={onCloseOthers}
      />
      <ConfirmDialog
        open={dialog === 'all'}
        onOpenChange={(open) => {
          if (!open) setDialog(null);
        }}
        title={t('sessions.confirm.close_all_title')}
        description={t('sessions.confirm.close_all_description')}
        confirmLabel={t('sessions.close_all')}
        destructive
        onConfirm={onCloseAll}
      />
    </>
  );
}
